import React from 'react'

import { returnGameFromStorage } from './logic'

import { Row, Col } from 'antd'

// 🔴 THE GRID IS NEUTRAL, NOT THE OLD AMBER (22 September 2026). It used to be
// `#f59e0b` / `#d97706` — the amber family of the theme this site retired when it moved to lime,
// so the most prominent graphic on the page was still painted in the colour the site had just
// left. It is now a neutral stone pair.
//
// 🔴 WHY NOT LIME, WHICH IS WHAT THE THEME WOULD SUGGEST — this was MEASURED by rendering all
// three and looking at them, not argued:
//   · lime at a similar value (`#65a30d` / `#84cc16`) — the two tones are too close, so the
//     alternation that marks the nine 3x3 boxes disappears and the board reads as one flat sheet.
//   · lime at a wide split (`#3f6212` / `#a3e635`) — the bright boxes now look SELECTED, i.e. the
//     grid implies a state that is not there, which is worse than a lost weave.
//   · a neutral `#a8a29e` / `#d6d3d1` — a different HUE at a similar VALUE, which is exactly what
//     the amber pair did and the only one of the three that marks the boxes without implying a
//     selection. It also keeps a visible boundary where two filled cells touch, so `8 5` side by
//     side cannot be read as `85`.
const borderTone = square => (square % 2 ? '#d6d3d1' : '#a8a29e')
// 🔴 LIGHTER THAN THEY WERE (George, 22 September 2026: "the cells with numbers filled in the
// background need a lighter shade or hue"). They were 0.48 and 0.34, which on the near-black
// olive read as a muddy patch rather than as a filled cell, and the darker of the two sat so close
// to an empty cell that telling them apart took a second look. The near-white digit still clears
// 3.2:1 on the lighter one, and the digit is 26px and bold, which is large text.
const fillOriginal = 'rgba(163, 230, 53, 0.62)'
const fillAnswered = 'rgba(163, 230, 53, 0.46)'
// A REJECTED DIGIT — one the player tried and the game refused. It is drawn in red so it
// cannot be mistaken for an answer, and it lives outside `puzzle` (see Play.js) so it never
// reaches the progress bar, the completion check or the conflict check.
const fillWrong = 'rgba(248, 113, 113, 0.28)'
const wrongText = '#f87171'

const Board = ({ gameId, rows, cellClick, mistakes }) => {
  // 🔴 A MISSING RECORD MUST NEVER CRASH THE PAGE (22 September 2026). This line used to be
  // `const { originalPuzzleFormatted } = returnGameFromStorage(gameId)`, and for a game that has
  // just been DELETED that call returns undefined — so destructuring threw inside render, React
  // unmounted the whole tree, and deleting one game from the saved list white-screened the app.
  // George reported it as "if i delete a game, i think its loading the game up"; the board did
  // not load, the page died. Every read here is guarded.
  const storedGame = returnGameFromStorage(gameId)
  const originalPuzzleFormatted = storedGame ? storedGame.originalPuzzleFormatted : null

  const isPuzzle = Boolean(cellClick)

  return rows.map((row, rowIndex) => {
    return (
      <Row
        key={`row_${rowIndex}`}
        justify='center'
        wrap={false}
      >
        {
          row.map((cell, cellIndex) => {
            const { key: cellKey, index, display, square } = cell

            const key = `${gameId}_${isPuzzle ? 'puzzle' : 'solution'}_${cellKey}`

            const marginBottom = [2, 5].includes(rowIndex) ? 6 : 1
            const marginRight = [2, 5].includes(cellIndex) ? 6 : 1

            const isAnswered = display !== null
            const isClickable = isPuzzle && !isAnswered

            const originalCell = originalPuzzleFormatted && originalPuzzleFormatted[rowIndex]
              ? originalPuzzleFormatted[rowIndex].find(i => i.key === cellKey)
              : null
            const isOriginal = Boolean(isPuzzle && originalCell && originalCell.display !== null)

            // 🔴 THE NUMBER-CLICK HIGHLIGHTING IS GONE (George, 22 September 2026: "i dont
            // like any of that highlighting just remove it"). Nothing on this board reacts
            // to a selected digit any more — the digit is chosen from the cell menu, so
            // there is no selected digit left to highlight.
            const mistake = !isAnswered && mistakes ? mistakes[index] : undefined
            const wrong = mistake ? mistake.value : undefined

            let backgroundColor
            if (isAnswered) backgroundColor = isOriginal ? fillOriginal : fillAnswered
            if (wrong !== undefined) backgroundColor = fillWrong

            const cursor = isClickable && 'pointer'

            const style = {
              // 🔴 THE CELLS ARE SQUARES (George, 22 September 2026: "check the square in
              // the game, they are no square"). They measured 49 x 36 — a RECTANGLE —
              // because the height was set by the digit's line box, not by the cell's
              // width. `aspectRatio` ties the height to the width, and the old 6px padding
              // is replaced by flex centring so the digit sits in the middle of the square.
              margin: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              // the column is now 940px, so a square is ~71px on a desktop: the old 18px cap
              // left the digit stranded in the middle of a big square. It still falls away to
              // 12px at handset width, where the square is ~24px.
              fontSize: 'clamp(12px, 3.4vw, 26px)',
              aspectRatio: '1 / 1',
              borderRadius: 2,
              border: `1px solid ${borderTone(square)}`,
              marginBottom,
              marginRight,
              backgroundColor,
              color: wrong !== undefined ? wrongText : (isAnswered ? '#f8fafc' : '#8b98a5'),
              fontWeight: isAnswered || wrong !== undefined ? 700 : 400,
              cursor
            }

            return (
              <Col
                key={key}
                span={2}
                onClick={() => isClickable && cellClick(cell)}
                style={style}
              >
                {isAnswered ? display : (wrong === undefined ? null : wrong)}
              </Col>
            )
          })
        }
      </Row>
    )
  })
}

export default Board
